import dayjs from 'dayjs';
import styled from 'styled-components';
import React, { Component } from 'react';
import { ArrowUpward as ArrowUpwardIcon } from '@material-ui/icons';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { push as Push, replace } from 'redux-first-history';
import { cloneDeep, find, forEach, isEmpty, map } from 'lodash-es';
import { Link } from '@material-ui/core';
import {
    BasicTableHeadersProps,
    BasicTableRowsProps,
    ContentLoader,
    CustomTable,
    EmptyState,
    formatNumber,
    MobileCardRowsProps,
    MobileTable,
    Pagination,
    palette,
    Panel,
    Paper,
    PrimaryButton,
    TertiaryButton,
    ToggleButtonGroup,
    Typography,
} from '@dev/ee-components';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { CampaignTemplate, CampaignType, CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';
import { parse } from 'query-string';

import { isCustomBranding } from '@/services/Secuirty';
import { AccountState } from '@/store/account/types';

import ExportModal from '../../../ExportModal';
import { addParamToUrl } from '../../../../services/tools/handleUrlParams';
import {
    CampaignReportLinksProps,
    LinksState,
    LinksToggleBtnOptInterface,
    LinkToggleBtnValues,
} from './CampaignReportLinksInterface';
import {
    AbSplitDisplayMode,
    CampaignsInterface,
    GetCampaignByNameActionParams,
} from '../../../../store/campaigns/types';
import { ExportLinks, GetLinkTrackingListStart, LinkTrackingParams } from '../../../../store/reports/actions';
import { ReportsState } from '../../../../store/reports/types';
import { CampaignsPaths } from '../../../../services/dictionaries/Paths';
import { ApplicationState, BrowserInterface } from '../../../../store';
import { ClearPreviewCampaign, GetCampaignByName, LoadPreviewTemplates } from '../../../../store/campaigns/actions';
import {
    getCampaignSimpleStatValue,
    getCampaignStartDate,
    injectTemplateClickCountsHtml,
    isClickTrackingEnabled,
} from '../Overview/helperFunctions';
import { DEFAULT_PER_PAGE_COUNT, LONG_DATE_FORMAT } from '../../../../services/constants';
import { links } from '../../../../services/dictionaries/links';
import { ShowDialog } from '../../../../store/dialog/actions';
import { ShowDialogParams } from '../../../../store/dialog/types';
import { ExportLinksParams } from '../../../../services/ElasticApi';
import { prepareAbSplitDataFromDate, prepareAbSplitDataToDate, sumStatFromAllLinks } from '../../helperFunctions';
import { DisabledTrackingAlert } from '../components/DisabledTrackingAlert';

const LinksWrapper = styled.div`
    padding-top: 32px;
`;

const IframeWrapper = styled.div`
    margin-top: 32px;
    border: 1px solid ${palette.gray[3]};

    & iframe {
        display: block;
        box-sizing: border-box;
        width: 100%;
        border: none;
        min-height: 500px;
    }
`;

const TopPanelsWrapper = styled.div<{ isMobile }>`
    display: ${({ isMobile }) => (isMobile ? 'block' : 'flex')};
    justify-content: space-between;
    .GridCart {
        margin-right: ${({ isMobile }) => (isMobile ? '0' : '32px')};
        flex-grow: 1;
        flex-basis: ${({ isMobile }) => (isMobile ? '100%' : '33%')};
        margin-bottom: ${({ isMobile }) => (isMobile ? '32px' : '0')};
        &:last-of-type {
            margin-right: 0px;
            margin-bottom: 0;
        }

        .MuiBox-root {
            display: ${({ isMobile }) => (isMobile ? 'inline-block' : 'inline')};
            margin-right: ${({ isMobile }) => (isMobile ? '12px' : '0')};
        }
        .MuiTypography-h2 {
            height: ${({ isMobile }) => (isMobile ? '0' : '')};
        }
    }
`;

const MobileTableWrapper = styled.div`
    & .MuiTableCell-body {
        overflow: auto;
        white-space: inherit;
        word-break: break-word;
    }
    & table {
        background: #2d37481a;
        margin: 4px 0;
    }
`;

const ContentLoaderWrapper = styled.div`
    margin-right: 16px;
    min-width: 45px;
    flex: 1;
`;

const AlertWrapper = styled.div`
    padding-top: 24px;
`;

const ButtonsRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 32px 0;
    height: 36px;
`;

const ContentWrapper = styled.div`
    background: ${palette.basic.paper};
    padding: 32px;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
`;
const LinkTable = styled.div`
    tr {
        word-break: break-all;
        td.single-row--cell {
            padding-block: 12px;
        }
    }
`;

interface StateToProps {
    browser?: BrowserInterface;
    reports?: ReportsState;
    campaigns: CampaignsInterface;
    account?: AccountState;
}

interface DispatchProps {
    GetLinkTrackingListStart: (params?: LinkTrackingParams) => void;
    Push?: (url: string) => void;
    GetCampaignByName: (params: GetCampaignByNameActionParams) => void;
    ShowDialog: (params: ShowDialogParams) => void;
    ExportLinks(params?: ExportLinksParams);
    Replace(param: Record<string, string> | string): void;
    ClearPreviewCampaign?: () => void;
}

export interface CampaignReportLinksState {
    toggleButtonValue: LinkToggleBtnValues;
    linksPerPage: number;
    linksActivePage: number;
}

type Props = StateToProps & DispatchProps & CampaignReportLinksProps;
class CampaignReportLinks extends Component<Props> {
    public iframeRef: any;

    public state: LinksState = {
        exportOptions: {
            from: null,
            to: null,
            channelName: this.props.match.params.name,
            fileName: null,
            compressionFormat: CompressionFormat.Zip,
            fileFormat: ExportFileFormats.Csv,
        },
        toggleButtonValue: LinkToggleBtnValues.Map,
        linksPerPage: DEFAULT_PER_PAGE_COUNT,
        linksActivePage: 1,
    };

    private fileNameLinksRef;

    public componentDidMount(): void {
        window.scrollTo(0, 0);
        const { GetCampaignByName, GetLinkTrackingListStart, match, campaigns } = this.props;
        const { name, type: campaignType } = match.params;
        const {
            currentPreview,
            pickedCampaign: { campaign },
            abSplitHeaderControls: { displayMode, currentTemplate },
        } = campaigns;
        const decodedName = safelyDecodeBase64Url(name);
        const isAbSplit = campaignType === CampaignType.AbSplit;

        const query = parse(location.search);

        const updateState = {
            ...(!isEmpty(query.perPage) ? { linksPerPage: Number(query.perPage) } : {}),
            ...(!isEmpty(query.activePage) ? { linksActivePage: Number(query.activePage) } : {}),
            ...(!isEmpty(query.selectedTab)
                ? { toggleButtonValue: query.selectedTab }
                : { toggleButtonValue: LinkToggleBtnValues.Map }),
        };

        if (!isEmpty(updateState)) {
            this.setState(updateState);
        }

        if (!isEmpty(query.selectedTab)) {
            this.addQueryToUrl({ selectedTab: query.selectedTab });
            this.handleChangeToggleButton(query.selectedTab);
        }

        if (!currentPreview?.templates || (isAbSplit && !campaign)) {
            GetCampaignByName({
                name: decodedName,
                campaignType,
                isLoadTemplatesRequired: true,
                isLoadLinkTrackingRequired: campaignType !== CampaignType.AbSplit,
                isSimpleStatsRequired: campaignType === CampaignType.AbSplit,
            });
        }

        if (campaign && isAbSplit) {
            const winnerTemplate: CampaignTemplate = find(
                campaign.CampaignTemplates,
                (template: CampaignTemplate) => template.IsWinner
            );

            GetLinkTrackingListStart({
                channelName: displayMode === AbSplitDisplayMode.Winner ? winnerTemplate.Name : currentTemplate,
                from: prepareAbSplitDataFromDate(campaign, displayMode),
                to: prepareAbSplitDataToDate(campaign, displayMode),
                isRequiredFullDateRange: true,
            });
        }

        if (!isAbSplit) {
            GetLinkTrackingListStart({ channelName: decodedName, isRequiredFullDateRange: true });
        }
    }

    public componentWillUnmount(): void {
        const linksOnMap = this.getAllLinksOnMap();
        forEach(linksOnMap, (el) => {
            el.removeEventListener('click', this.linkClicksOnIframe.bind(this));
        });
        this.props.ClearPreviewCampaign();
    }

    public componentDidUpdate(prevProps: Props): void {
        const {
            campaigns: { pickedCampaign },
            GetLinkTrackingListStart,
            match,
        } = this.props;
        const { campaign } = pickedCampaign;
        if (match.params.type === CampaignType.AbSplit) {
            const { currentTemplate: prevTemplate, displayMode: prevDisplayMode } =
                prevProps.campaigns.abSplitHeaderControls;
            const { currentTemplate, displayMode: currentDisplayMode } = this.props.campaigns.abSplitHeaderControls;

            if (prevTemplate !== currentTemplate) {
                GetLinkTrackingListStart({
                    channelName: currentTemplate,
                    from: campaign.TriggerOptions.DateToSendOn,
                    to: prepareAbSplitDataToDate(campaign, currentDisplayMode),
                    isRequiredFullDateRange: true,
                });
            }

            if (prevDisplayMode !== currentDisplayMode) {
                const abSplitDataFromDate = prepareAbSplitDataFromDate(campaign, currentDisplayMode);
                const winnerTemplate: CampaignTemplate = find(
                    campaign.CampaignTemplates,
                    (template: CampaignTemplate) => template.IsWinner
                );

                GetLinkTrackingListStart({
                    channelName:
                        currentDisplayMode === AbSplitDisplayMode.Winner ? winnerTemplate.Name : currentTemplate,
                    from: abSplitDataFromDate,
                    to: prepareAbSplitDataToDate(campaign, currentDisplayMode),
                    isRequiredFullDateRange: true,
                });
            }
        }
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    private getTopPanelStatistics(): JSX.Element {
        const {
            t,
            campaigns: { pickedCampaign, loading, abSplitHeaderControls },
            reports: { linkTrackingList, isLoadingLinks },
            account: { AdvancedOptions },
            match: {
                params: { type },
            },
            browser,
        } = this.props;
        const { currentTemplate, displayMode } = abSplitHeaderControls;
        const { simpleStats, campaign } = pickedCampaign;
        const isAbSplit = type === CampaignType.AbSplit;

        const Clicks = sumStatFromAllLinks('Clicks', linkTrackingList);
        const UniqueClicks = getCampaignSimpleStatValue(
            pickedCampaign?.campaign,
            'Clicked',
            pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
            abSplitHeaderControls.currentTemplate,
            abSplitHeaderControls.displayMode
        );

        const ClickedRate = isAbSplit
            ? getCampaignSimpleStatValue(
                  campaign,
                  'ClickedPercentage',
                  simpleStats?.CampaignTemplatesSummary,
                  currentTemplate,
                  displayMode
              )
            : pickedCampaign?.campaign?.Stats?.ClickedPercentage || 0;

        const isClicksTracked = isClickTrackingEnabled(campaign, AdvancedOptions);
        const isMobile = browser.lessThan.hd;

        return (
            <>
                <TopPanelsWrapper isMobile={isMobile}>
                    <Panel
                        description={
                            !isLoadingLinks && !isEmpty(pickedCampaign.campaign) ? (
                                <>
                                    <Typography variant={'h1'} display="inline">
                                        {isClicksTracked ? formatNumber(UniqueClicks) : t('na')}
                                    </Typography>
                                    <Typography variant={'h3'} display="inline">
                                        {' '}
                                        {t('usersclicked')}
                                    </Typography>
                                    <Typography variant={'body1'} bodyVariant={'help'}>
                                        {t('numberofuniqueclicks')}
                                    </Typography>
                                </>
                            ) : (
                                this.getPanelsLoadingContent()
                            )
                        }
                        isMobile={isMobile}
                        headerPadding={isMobile ? '24px 32px' : '32px 40px'}
                    />
                    <Panel
                        isMobile={isMobile}
                        headerPadding={isMobile ? '24px 32px' : '32px 40px'}
                        description={
                            !isLoadingLinks && !isEmpty(pickedCampaign.campaign) ? (
                                <>
                                    <Typography variant={'h1'} display="inline">
                                        {isClicksTracked ? formatNumber(Clicks) : t('na')}
                                    </Typography>
                                    <Typography variant={'h3'} display="inline">
                                        {' '}
                                        {t('totalclicks')}
                                    </Typography>
                                    <Typography variant={'body1'} bodyVariant={'help'}>
                                        {t('numberoftotalclicksofalllinks')}
                                    </Typography>
                                </>
                            ) : (
                                this.getPanelsLoadingContent()
                            )
                        }
                    />
                    <Panel
                        isMobile={isMobile}
                        headerPadding={isMobile ? '24px 32px' : '32px 40px'}
                        description={
                            !isLoadingLinks && !loading && !isEmpty(pickedCampaign.campaign) ? (
                                <>
                                    <Typography variant={'h1'} display="inline">
                                        {isClicksTracked ? `${formatNumber(ClickedRate)}%` : t('na')}
                                    </Typography>
                                    <Typography variant={'h3'} display="inline">
                                        {' '}
                                        {t('clickedrate')}
                                    </Typography>
                                    <div>
                                        {isCustomBranding() ? (
                                            <div />
                                        ) : (
                                            <Link target="_blank" rel="noreferrer" href={links.openAndClickRates}>
                                                {t('learnmore')}
                                            </Link>
                                        )}
                                    </div>
                                </>
                            ) : (
                                this.getPanelsLoadingContent()
                            )
                        }
                    />
                </TopPanelsWrapper>
                {!loading && !isEmpty(pickedCampaign.campaign) && (
                    <AlertWrapper>
                        <DisabledTrackingAlert />
                    </AlertWrapper>
                )}
            </>
        );
    }

    private getPanelsLoadingContent(): JSX.Element {
        return (
            <ContentLoaderWrapper>
                <ContentLoader mode={'row'} height={19} />
                <ContentLoader mode={'row'} height={19} />
            </ContentLoaderWrapper>
        );
    }

    private getToggleButton(): JSX.Element {
        const { t } = this.props;
        const { toggleButtonValue } = this.state;

        const toggleBtnOptions: LinksToggleBtnOptInterface[] = [
            {
                label: t('clickmap'),
                value: LinkToggleBtnValues.Map,
            },
            {
                label: t('clicklist'),
                value: LinkToggleBtnValues.List,
            },
        ];

        return (
            <ToggleButtonGroup
                defaultValue={LinkToggleBtnValues.Map}
                size="small"
                options={toggleBtnOptions}
                onChange={this.handleChangeToggleButton.bind(this)}
                value={toggleButtonValue}
            />
        );
    }

    private handleChangeToggleButton(newValue): void {
        this.setState({ toggleButtonValue: newValue });
        this.addQueryToUrl({ selectedTab: newValue });
    }

    private addQueryToUrl(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private changePage = (activePage: number, perPage: number): void => {
        this.setState({ linksPerPage: perPage, linksActivePage: activePage });
        this.addQueryToUrl({ perPage, activePage });
    };

    private getTableWithLinks(): JSX.Element {
        const {
            t,
            reports,
            browser,
            match,
            campaigns: { abSplitHeaderControls, pickedCampaign },
            Push,
        } = this.props;
        const { linksActivePage, linksPerPage } = this.state;
        const { currentTemplate, displayMode } = abSplitHeaderControls;
        const { campaign } = pickedCampaign;

        const isAbSplit = match.params.type === CampaignType.AbSplit;
        let viewContactsChannelName = isAbSplit ? currentTemplate : campaign?.Name;

        if (isAbSplit) {
            const winnerTemplateChannel: CampaignTemplate = find(
                campaign?.CampaignTemplates,
                (template) => template.IsWinner
            );
            viewContactsChannelName =
                displayMode === AbSplitDisplayMode.TestResult ? currentTemplate : winnerTemplateChannel?.Name;
        }
        const isMobile = browser.lessThan.hd;
        const headerName: BasicTableHeadersProps[] = [
            {
                id: 'link',
                label: t('links'),
            },
            {
                id: 'uniqueclicks',
                disablePadding: false,
                label: t('uniqueclicks'),
                headerAlign: 'right',
            },
            {
                id: 'totalclicks',
                disablePadding: false,
                label: t('clicks'),
                headerAlign: 'right',
            },
            {
                id: 'viewcontacts',
                label: '',
            },
        ];
        const mobileHeader = [t('links'), t('uniqueclicks'), t('clicks')];

        const columnWidth: Record<string, string> = {
            link: '53%',
            uniqueclick: '17%',
            click: '15%',
            viewcontacts: '15%',
        };
        const isEmptyState = !reports.linkTrackingList.length && !reports.linkTrackingLoading;
        const isMobileTable = !!reports.linkTrackingList.length;
        return (
            <>
                {isMobile && (
                    <>
                        {isEmptyState && (
                            <ContentWrapper>
                                <EmptyState text={t('emptystate')} />
                            </ContentWrapper>
                        )}
                        {isMobileTable && (
                            <>
                                <MobileTableWrapper>
                                    <MobileTable
                                        headers={mobileHeader}
                                        rows={this.getTableWithLinksRowsMobile()}
                                        buttonText={t('viewcontacts')}
                                        onClickButton={(ev, rows): void => {
                                            Push(
                                                `${CampaignsPaths.CampaignLinkContacts.replace(
                                                    ':type',
                                                    match.params.type
                                                )
                                                    .replace(':name', match.params.name)
                                                    .replace(
                                                        ':link',
                                                        safelyEncodeBase64Url(rows.link as string)
                                                    )}?templateName=${viewContactsChannelName}`
                                            );
                                        }}
                                    />
                                </MobileTableWrapper>

                                <ContentWrapper>
                                    <Pagination
                                        perPage={linksPerPage}
                                        activePage={linksActivePage}
                                        dataLength={reports?.linkTrackingList?.length || 0}
                                        handlePaginationChange={(activePage, perPage): void =>
                                            this.changePage(activePage, perPage)
                                        }
                                        disableBorderTop={true}
                                    />
                                </ContentWrapper>
                            </>
                        )}
                    </>
                )}
                {!isMobile && (
                    <Paper padding={{ top: isMobile ? 0 : 24 }}>
                        <LinkTable>
                            <CustomTable
                                headers={headerName}
                                rows={this.getTableWithLinksRows()}
                                isEmpty={!reports.linkTrackingList.length && !reports.linkTrackingLoading}
                                loading={reports.linkTrackingLoading}
                                stickyHeader
                                rowPadding={48}
                                rowHeight={20}
                                hideCheckboxes
                                columnsWidth={columnWidth}
                                emptyStateProps={{ text: t('emptystate') }}
                                disableRowClick
                                disableFirstRowBorderTop
                            />
                        </LinkTable>
                        <Pagination
                            perPage={linksPerPage}
                            activePage={linksActivePage}
                            dataLength={reports?.linkTrackingList?.length || 0}
                            handlePaginationChange={(activePage, perPage): void => this.changePage(activePage, perPage)}
                        />
                    </Paper>
                )}
            </>
        );
    }

    private linkClicksOnIframe(ev) {
        const {
            Push,
            match: { params },
            campaigns,
        } = this.props;
        ev.preventDefault();
        const {
            abSplitHeaderControls: { displayMode, currentTemplate },
            pickedCampaign: { campaign },
        } = campaigns;

        const href = ev?.currentTarget.getAttribute('href');
        const isAbSplit = params.type === CampaignType.AbSplit;
        let viewContactsChannelName = isAbSplit ? currentTemplate : campaign.Name;
        if (isAbSplit) {
            const winnerTemplateChannel: CampaignTemplate = find(
                campaign.CampaignTemplates,
                (template) => template.IsWinner
            );
            viewContactsChannelName =
                displayMode === AbSplitDisplayMode.TestResult ? currentTemplate : winnerTemplateChannel.Name;
        }
        const updatedHref = `${CampaignsPaths.CampaignLinkContacts.replace(':type', params?.type)
            .replace(':name', params?.name)
            .replace(':link', safelyEncodeBase64Url(href))}?templateName=${viewContactsChannelName}`;

        return Push(updatedHref);
    }

    private getAllLinksOnMap = () => this.iframeRef?.contentWindow?.document?.querySelectorAll?.('.linkWithTooltip');

    private onIframeLoad(): void {
        this.iframeRef.style.height = `${this.iframeRef.contentWindow.document.body.scrollHeight}px`;
        const elements = this.getAllLinksOnMap();
        forEach(elements, (el) => {
            el.addEventListener('click', this.linkClicksOnIframe.bind(this));
        });
    }

    private renderTemplatePreview(): JSX.Element {
        const {
            campaigns,
            reports: { linkTrackingList },
            match,
            t,
            browser,
        } = this.props;
        const isMobile = browser.lessThan.hd;
        const {
            abSplitHeaderControls: { displayMode, currentTemplate },
            currentPreview: { templates, isTemplateDeleted },
            pickedCampaign: { campaign },
        } = campaigns;

        if (isTemplateDeleted) {
            return <div>{t('templatedeleted')}</div>;
        }

        if (!templates?.length && !isTemplateDeleted) {
            return <ContentLoader mode="circle" />;
        }
        const isAbSplit = match.params.type === CampaignType.AbSplit;
        const pickedTemplate = isAbSplit
            ? find(templates, (template) => {
                  if (displayMode === AbSplitDisplayMode.Winner) {
                      const winnerTemplate = campaign.CampaignTemplates.find((template) => template.IsWinnerTemplate);
                      return winnerTemplate.Name === template.channelName;
                  }

                  return template.channelName === currentTemplate;
              })
            : templates[0];

        const templateHtml = linkTrackingList.length
            ? injectTemplateClickCountsHtml(pickedTemplate.html, linkTrackingList, isMobile)
            : pickedTemplate.html;

        return (
            <IframeWrapper>
                <iframe
                    ref={(element) => (this.iframeRef = element)}
                    onLoad={this.onIframeLoad.bind(this)}
                    title={pickedTemplate.name}
                    srcDoc={templateHtml}
                />
            </IframeWrapper>
        );
    }

    private getTableWithLinksRows = (): BasicTableRowsProps[] => {
        const {
            t,
            Push,
            reports,
            match,
            campaigns: { abSplitHeaderControls, pickedCampaign },
        } = this.props;
        const { linksActivePage, linksPerPage } = this.state;
        const { currentTemplate, displayMode } = abSplitHeaderControls;
        const { campaign } = pickedCampaign;

        if (!campaign) {
            return [];
        }

        const isAbSplit = match.params.type === CampaignType.AbSplit;
        const offset = (linksActivePage - 1) * linksPerPage;
        let viewContactsChannelName = isAbSplit ? currentTemplate : campaign.Name;

        if (isAbSplit) {
            const winnerTemplateChannel: CampaignTemplate = find(
                campaign.CampaignTemplates,
                (template) => template.IsWinner
            );
            viewContactsChannelName =
                displayMode === AbSplitDisplayMode.TestResult ? currentTemplate : winnerTemplateChannel.Name;
        }

        return map(reports.linkTrackingList.slice(offset, offset + linksPerPage), (singleLink) => ({
            link: singleLink.Link,
            uniqueclick: (
                <div style={{ textAlign: 'right' }}>{formatNumber(parseInt(singleLink.UniqueClicks, 10))}</div>
            ),
            click: <div style={{ textAlign: 'right' }}>{formatNumber(parseInt(singleLink.Clicks, 10))}</div>,
            viewcontacts: (
                <div style={{ textAlign: 'right' }}>
                    <TertiaryButton
                        text={t('viewcontacts')}
                        size="small"
                        onClick={(): void => {
                            Push(
                                `${CampaignsPaths.CampaignLinkContacts.replace(':type', match.params.type)
                                    .replace(':name', match.params.name)
                                    .replace(
                                        ':link',
                                        safelyEncodeBase64Url(singleLink.Link)
                                    )}?templateName=${viewContactsChannelName}`
                            );
                        }}
                    />
                </div>
            ),
            rowID: singleLink.Link,
        }));
    };

    private getTableWithLinksRowsMobile = (): MobileCardRowsProps[] => {
        const {
            reports,
            campaigns: { pickedCampaign },
        } = this.props;
        const { linksActivePage, linksPerPage } = this.state;
        const { campaign } = pickedCampaign;

        if (!campaign) {
            return [];
        }
        const SkeletonTemplate = {
            link: <ContentLoader mode="row" />,
            uniqueclick: <ContentLoader mode="row" />,
            click: <ContentLoader mode="row" />,
        };
        const offset = (linksActivePage - 1) * linksPerPage;

        const SkeletonRows = map(
            [...new Array(reports.linkTrackingList.slice(offset, offset + linksPerPage))],
            (): MobileCardRowsProps => SkeletonTemplate
        );

        const rows = map(reports.linkTrackingList.slice(offset, offset + linksPerPage), (singleLink) => ({
            link: singleLink.Link,
            uniqueclick: formatNumber(parseInt(singleLink.UniqueClicks, 10)),
            click: formatNumber(parseInt(singleLink.Clicks, 10)),
        }));
        return reports.isLoadingLinks ? SkeletonRows : rows;
    };

    private getExportButton(): JSX.Element {
        const {
            t,
            reports: { linkTrackingList },
        } = this.props;

        return (
            <PrimaryButton
                id="exportlinks"
                onClick={this.openExportLinksModal.bind(this)}
                disabled={isEmpty(linkTrackingList) ? true : false}
                size="medium"
                text={t('export')}
                variant="text"
                startIcon={<ArrowUpwardIcon />}
            />
        );
    }

    private openExportLinksModal(): void {
        const { ShowDialog, t, browser } = this.props;
        const isMobile = browser.lessThan.hd;

        ShowDialog({
            title: t('exportlinks'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={(element) => {
                        this.fileNameLinksRef = element;
                    }}
                    isMobile={isMobile}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportLinks.bind(this),
        });
    }

    private changeExportCompression(isEnabled: boolean): void {
        const { exportOptions: prevExportOptions } = this.state;

        const exportOptions = cloneDeep(prevExportOptions);
        exportOptions.compressionFormat = isEnabled ? CompressionFormat.Zip : CompressionFormat.None;

        this.setState({ exportOptions });
    }

    private changeExportFileFormat(data: { value: ExportFileFormats; label: string }): void {
        const { exportOptions: prevExportOptions } = this.state;

        const exportOptions = cloneDeep(prevExportOptions);
        exportOptions.fileFormat = data.value;

        this.setState({ exportOptions });
    }

    private exportLinks(): void {
        const {
            ExportLinks,
            match,
            campaigns: { pickedCampaign },
        } = this.props;

        const { exportOptions } = this.state;
        const fileName = this.fileNameLinksRef.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;

        const exportParams = {
            channelName: safelyDecodeBase64Url(match.params.name),
            from: dayjs(getCampaignStartDate(pickedCampaign?.campaign, LONG_DATE_FORMAT)).startOf('day').format(),
            to: dayjs().format(LONG_DATE_FORMAT),
            fileFormat,
            compressionFormat: exportOptions.compressionFormat,
            fileName,
        };

        ExportLinks(exportParams);
    }

    public render(): JSX.Element {
        const { toggleButtonValue } = this.state;

        return (
            <LinksWrapper>
                {this.getTopPanelStatistics()}
                {
                    <ButtonsRow>
                        {this.getToggleButton()}
                        {toggleButtonValue === LinkToggleBtnValues.List ? this.getExportButton() : null}
                    </ButtonsRow>
                }
                {toggleButtonValue === LinkToggleBtnValues.Map
                    ? this.renderTemplatePreview()
                    : this.getTableWithLinks()}
            </LinksWrapper>
        );
    }
}

const dispatchedActions = {
    Push,
    GetLinkTrackingListStart,
    GetCampaignByName,
    LoadPreviewTemplates,
    ShowDialog,
    ExportLinks,
    Replace: replace,
    ClearPreviewCampaign,
};

const mapStateToProps = (state: ApplicationState, ownProps: Props): StateToProps => ({
    ...ownProps,
    browser: state.browser,
    reports: state.reports,
    campaigns: state.campaigns,
    account: state.account,
});

export default compose(
    withTranslation(),
    connect<StateToProps, DispatchProps, Props>(mapStateToProps, dispatchedActions)
)(CampaignReportLinks);
